import { createContext, type ReactNode, useContext } from 'react';
import { type OpenAtViewOptions, type View } from '@cx-web/feature-myoc-login/store';

/**
 * The CX runtime adapter (NGF-6334) — the thin portability layer that lets a page-template component
 * be authored ONCE and consumed by both the React Router 7 host (apps/atlas) and the Astro PoC
 * (apps/atlas-astro). It captures the framework "seams" that are the ONLY genuine differences
 * between the two forks of a component; everything else (reducer, validation, derived state, view,
 * CSS) is shared. Each app implements this adapter exactly once and mounts it via `CxRuntimeProvider`
 * (atlas: high in the page-template provider tree; atlas-astro: inside each island root, since React
 * context cannot cross the Astro↔island boundary).
 *
 * Each member is a HOOK (returns its value), so a shared component calls them unconditionally at the
 * top level — e.g. `const t = useCxRuntime().useLabel()` — keeping the Rules of Hooks satisfied.
 *
 * Each seam exposes a CAPABILITY, not a single caller's action (e.g. the auth seam is the modal's
 * general `openAtView`, not `openSignup`), so a new consumer/interaction needs no change here or in
 * either adapter. Responsive behaviour is deliberately NOT a seam — express it in CSS (media queries
 * / `<picture>`), which is framework-agnostic and SSR-safe by construction; a JS media hook would
 * reintroduce UA-seed fragility and a post-mount layout flip across two SSR frameworks.
 */
export interface CxRuntimeAdapter {
  /**
   * Auth-modal seam — the modal's general open-at-view capability. atlas: AuthModalProvider context
   * `openAtView`; atlas-astro: the published cross-island store via `openAuthModalAtView`. Callers
   * pick the view, so login/reset/etc. need no new method on the seam or its implementations.
   */
  useAuthModal: () => { openAtView: (view: View, options?: OpenAtViewOptions) => void };
  /** Static asset config. atlas: `useUIConfig()`; atlas-astro: island prop. */
  useConfig: () => { resourcesUrl: string };
  /** Label/i18n lookup. atlas: LabelProvider `get`; atlas-astro: react-i18next with RESPONSIVE_ prefix. */
  useLabel: () => (key: string) => string;
  /** Analytics. atlas: `new TrackingActions().eventTracking()`; atlas-astro: `trackEvent` (dataLayer). */
  useTracking: () => { track: (event: string, payload: Record<string, unknown>) => void };
}

const CxRuntimeContext = createContext<CxRuntimeAdapter | null>(null);

/**
 * Mounts a CX runtime adapter for descendant shared components. Implemented as an explicit wrapper
 * component rather than aliasing `CxRuntimeContext.Provider`: a bare `.Provider` re-export can be
 * mis-resolved as the context's `.Consumer` in some production bundles (manifesting as a "children
 * is not a function" crash when given element children). A real component is bundle-robust.
 */
export const CxRuntimeProvider = ({ children, value }: { children: ReactNode; value: CxRuntimeAdapter }) => (
  <CxRuntimeContext.Provider value={value}>{children}</CxRuntimeContext.Provider>
);

/**
 * Read the active CX runtime adapter. Throws if no provider is mounted — a loud dev-time failure that
 * catches the context-across-island-boundary pitfall (a missed island wrapper) instead of silently
 * rendering a broken component.
 */
export const useCxRuntime = (): CxRuntimeAdapter => {
  const adapter = useContext(CxRuntimeContext);
  if (!adapter) {
    throw new Error('useCxRuntime must be used within a CxRuntimeProvider');
  }
  return adapter;
};
